import { Component, type ErrorInfo, type ReactNode } from 'react';
import redirectToPlatform from '@services/general';

interface SessionErrorBoundaryProps {
  readonly section: string;
  readonly children: ReactNode;
}

interface SessionErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Catches render errors and recovers gracefully.
 *
 * When any render error is detected in the AgentGardenHome component tree,
 * the page is reloaded up to 3 times to recover from transient DOM state issues.
 * After 3 failed reloads, redirects to home to prevent infinite loops.
 * Errors are logged for debugging purposes.
 */
const RELOAD_COUNTER_KEY = 'SessionErrorBoundary_reloadCount';
const MAX_RELOADS = 3;

export default class SessionErrorBoundary extends Component<
SessionErrorBoundaryProps,
SessionErrorBoundaryState
> {
  constructor(props: SessionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SessionErrorBoundaryState {
    // Get current reload count from sessionStorage
    const reloadCount = parseInt(
      sessionStorage.getItem(RELOAD_COUNTER_KEY) || '0',
      10,
    );

    console.warn('[SessionErrorBoundary] ⚠️ Error detected in component tree', error);

    if (reloadCount < MAX_RELOADS) {
      console.warn(
        `[SessionErrorBoundary] 🔄 Reloading page... (${reloadCount + 1}/${MAX_RELOADS})`,
      );
      // Increment counter and reload
      sessionStorage.setItem(RELOAD_COUNTER_KEY, String(reloadCount + 1));
      window.location.reload();
    } else {
      console.warn('[SessionErrorBoundary] ❌ Max reloads exceeded - redirecting to platform');
      // Clear counter and redirect to platform
      sessionStorage.removeItem(RELOAD_COUNTER_KEY);
      redirectToPlatform();
    }

    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { section } = this.props;
    console.error(
      `[SessionErrorBoundary] "${section}" failed to render: ${error.message}`,
      errorInfo.componentStack ?? '',
    );
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return null;
    }
    return children;
  }
}
